import React from 'react';
import { useTheme } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
import { DocData } from 'src/docs/types';
import { useMobile } from 'src/utils/useWindowSize';
import { DocsProvider } from './DocsContext';
import DocsScrollManager from './DocsScrollManager';
import TOC, { tocWidth } from './TOC';
import Heading from './Heading';
import CodeBlock from './CodeBlock';
import LinkBlock from './LinkBlock';

interface Props {
  docData: DocData;
}

export default function Docs({ docData }: Props): React.ReactElement {
  const mobile = useMobile();
  const theme = useTheme();

  return (
    <DocsProvider>
      <DocsScrollManager docData={docData}>
        <TOC meta={docData.meta} />
        <Box
          sx={{
            marginRight: `${tocWidth}px`,
            [theme.breakpoints.down('md')]: {
              marginRight: 0,
            },
          }}
        >
          <Container
            className="markdown-body"
            maxWidth="md"
            component="article"
            disableGutters={mobile}
            sx={{
              paddingBottom: 60,
              color: theme.palette.text.primary,
              '& table tr': {
                backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#222',
              },
              '& table tr:nth-child(2n)': {
                backgroundColor: theme.palette.mode === 'light' ? '#f6f8fa' : '#333',
              },
              '& blockquote': {
                backgroundColor: theme.palette.mode === 'light' ? '#eee' : '#555',
              },
            }}
          >
            <h1
              style={{
                fontSize: '2.5em',
              }}
            >
              {docData.meta.title}
            </h1>
            <ReactMarkdown allowDangerousHtml renderers={{ heading: Heading(docData.meta), code: CodeBlock, link: LinkBlock }} plugins={[gfm]}>
              {docData.content}
            </ReactMarkdown>
          </Container>
        </Box>
      </DocsScrollManager>
    </DocsProvider>
  );
}
